<template>
  <tr
    :id="`field-item-${index}`"
    :key="`db-row-${field.fieldGuid}`"
    class="field-item table-builder-field text-center"
    :class="{'invalid-row': invalidFieldMap[field.fieldGuid]}"
  >
    <td>
      <div
        :id="`field-${fieldItemIndex}-handle`"
        class="handle"
      >
        <font-awesome-icon :icon="['fas', 'bars']" />
      </div>
      <div
        v-b-tooltip.hover.right="'Add new field between'"
        class="icon-wrapper"
        @click.stop="$emit('on-add-field-between', index)"
      >
        <font-awesome-icon
          v-if="!isLastItem"
          class="add-between-icon"
          :icon="['fas', 'plus']"
        />
      </div>
    </td>
    <td>
      <fieldset class="w-100">
        <div
          v-b-tooltip.hover
          :title="isDisabledToDelete ? 'Field cannot be renamed, it is currently in use in a workflow.' : ''"
        >
          <b-input
            :id="`field-${fieldItemIndex}-label`"
            ref="label"
            :value="field.displayName"
            class="mr-sm-2"
            :class="classPrefix + 'field-label'"
            placeholder="Field Label"
            :disabled="isPosted || isDisabledToDelete || isDisabled"
            @input="handleFieldChange('displayName', $event)"
          />
        </div>
      </fieldset>
    </td>
    <td>
      <fieldset class="w-100">
        <field-type-select
          :id="`field-${fieldItemIndex}-type`"
          :field="field"
          :options="getOptions(field)"
          :disabled="fieldIsDisabled"
          :class-names="'mr-sm-2 '+classPrefix+'field-type'"
          @change="handleFieldChange('dataTypeName', $event)"
          />
      </fieldset>
    </td>
    <td>
      <div class="w-100 text-right">
        <b-form-checkbox
          :id="`field-${fieldItemIndex}-required`"
          :checked="field.isRequired"
          :disabled="isPosted || isDisabled"
          @change="handleFieldChange('isRequired', $event)"
        />
      </div>
    </td>
    <td>
      <div class="w-100 text-right">
        <b-form-checkbox
          :id="`field-${fieldItemIndex}-displayInTable`"
          :checked="field.displayInTable"
          :disabled="isPosted || isDisabled"
          @change="handleFieldChange('displayInTable', $event)"
        />
      </div>
    </td>
    <td>
      <div class="w-100 text-right">
        <b-form-checkbox
          :id="`field-${fieldItemIndex}-displayInForm`"
          :checked="field.displayInForm"
          :disabled="isPosted || isDisabled"
          @change="handleFieldChange('displayInForm', $event)"
        />
      </div>
    </td>
    <td>
      <div class="w-100 text-right">
        <b-form-checkbox
          :id="`field-${fieldItemIndex}-isBusinessKey`"
          :checked="field.isBusinessKey"
          :disabled="isPosted || isDisabled || field.dataTypeName === 'document'"
          @change="handleFieldChange('isBusinessKey', $event)"
        />
      </div>
    </td>
    <td>
      <div class="w-100 text-right">
        <b-form-checkbox
          :id="`field-${fieldItemIndex}-isUnique`"
          :checked="field.isUnique"
          :disabled="field.dataTypeName === 'document'"
          @change="handleFieldChange('isUnique', $event)"
        />
      </div>
    </td>
    <td>
      <div class="d-flex action-buttons text-nowrap justify-content-end">
        <span
          v-show="fieldRequiresSave"
          :id="`${fieldItemIndex}-btn-wrapper`"
        >
          <b-button
            :id="`field-${fieldItemIndex}-save`"
            class="btn-save-wrapper"
            title=""
            :disabled="isDisabledToSave || isDisabled"
            variant="link"
            pill
            @click="handleFieldSave"
          >
            <b-spinner
              v-if="fieldItemCalculationLoadingState === field.fieldGuid"
              class="save-loading"
              small
            />
            <font-awesome-icon
              v-else
              class="text-success btn-save"
              icon="save"
            />
          </b-button>
        </span>
        <b-tooltip
          placement="left"
          :target="`${fieldItemIndex}-btn-wrapper`"
          triggers="hover"
        >
          <span v-if="saveFieldProhibitedMessage">
            {{saveFieldProhibitedMessage}}
          </span>
        </b-tooltip>
        <!--          :id="`field-${fieldItemIndex}-permissions`"-->
        <b-button
          v-b-tooltip.hover
          variant="link"
          pill
          class="btn-permission"
          title="Manage Permission"
          :disabled="isPosted || isDisabled"
          @click="changePermission"
        >
          <font-awesome-icon :icon="'key'" />
        </b-button>
        <!--          :id="`field-${fieldItemIndex}-settings`"-->
        <b-button
          variant="link"
          pill
          class="btn-settings"
          :disabled="isPosted || isDisabled"
          @click="onSettingsClick"
        >
          <font-awesome-icon :icon="'cog'" />
        </b-button>

        <!-- Field Calculation Modal icon -->
        <div
          v-b-tooltip.hover
          v-if="fieldCalculationData.modalSupported"
          :title="calculationDataValidationFailureMessage || fieldCalculationDeniedMessage || (fieldHasCalculation ? 'Configure calculation' : 'Set calculation')"
        >
          <b-button
            variant="link"
            pill
            class="btn-calculation"
            :class="fieldCalculationModalIconClassNames"
            title=""
            @click="onFieldCalculationModalClick"
          >
            <font-awesome-icon  :icon="'calculator'" />
          </b-button>
        </div>

        <!-- Field Deletion icon -->
        <div
          v-b-tooltip.hover
          :title="deleteFieldProhibitedMessage"
        >
          <!--            :id="`field-${fieldItemIndex}-delete`"-->
          <b-button
            variant="link"
            pill
            class="btn-delete text-danger"
            title=""
            :disabled="isDisabledToDelete || isDisabled"
            @click="onDeleteClick"
          >
            <font-awesome-icon :icon="'trash'" />
          </b-button>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
import {isEqual} from 'lodash-es';
import { cloneDeep } from 'lodash';
import * as entityUtils from '@/core/utils/entityUtils.js';
import * as jsonUtils from '@/core/utils/jsonUtils.js';
/* Components */
import FieldTypeSelect from '../components/FieldTypeSelect.vue';

/* State */
const {
  mapState:dataManagementMapState,
  mapActions:dataManagementMapActions,
  mapGetters:dataManagementMapGetters,
} = createNamespacedHelpers('dataManagement');
const {mapState: appMapState} = createNamespacedHelpers('app');

export default {
  name: 'FieldItem',
  components: { FieldTypeSelect },
  inject: [
    /** TableBuilder Field Calculation configuration. */
    'fieldCalculationData',
    /** TableBuilder function to open Field Calculation modal when icon clicked. */
    'showFieldCalculationModal',
  ],
  props: {
    isErrorDuringUpdateExist: Object,
    index: Number,
    field: {
      required: true,
      type: Object,
    },
    isFieldChanged: Boolean,
    invalidFieldMap: {
      type: Object,
    },
    fieldTypeMap: {
      type: Object,
    },
    classPrefix: {
      type: String,
    },
    isTableEdit: {
      type: Boolean,
      default: false,
    },
    isDisabledToSave: {
      type: Boolean,
      default: true,
    },
    saveFieldProhibitedMessage: {
      type: String,
      default: null,
    },
    isDisabledToDelete: {
      type: Boolean,
      default: false,
    },
    deleteFieldProhibitedMessage: {
      type: String,
      default: null,
    },
    isPosted: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isLastItem: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      copy: null,
      isChanged: false,
      isSaved: false,
      rules: 'required|alpha_dash',
      attribute: 'name',
      ruleMessages: {
        required: 'This field is required',
        alpha_dash: 'Field can only contain alpha-numeric characters as well as dashes and underscores',
      },
    };
  },
  watch: {
    /**
     * Watch: isErrorDuringUpdateExist - prop from TableBuilder
     * - Value is modified in TableBuilder.updateFieldHandler() <- TableBuilder.onUpdateFields() <- DataManagementState.updateFields().
     * - Value is modified in TableBuilder.onSubmit() <- as above.
     */
    isErrorDuringUpdateExist: {
      handler(val) {
        if(!!val.targetField){
          if(val.targetField === this.field.fieldGuid){
            if (val.isError){
              this.isChanged = true;
              this.isSaved = false;
            }else{
              this.isChanged = false;
              this.isSaved = true;
            }
          };
        }else{
          if (val.isError) {
            this.isChanged = !!val.fields?.find(f => f.fieldGuid === this.field.fieldGuid);
            this.isSaved = false;
          }else{
            this.isChanged = false;
            this.isSaved = true;
          }
        }
      },
    },
    /**
     * Watch: clonedItems - computed on this.field.
     * - Compares the new definition of the Field to a previously cloned Copy.
     * - this.copy is a deep copy of this.field when the component is mounted.
     * - Apr25 AH Doesn't look as if this.copy is ever updated so remains the field when component was created.
     * - So, any changes to the Field definition will cause isChanged to be set and the 'Save' icon to be displayed.
     * - Also, some properties of the Field differ depending on whether it is an API response or constructed by one of the modals,
     * eg. calculationJSON capitalisation of top level property names (FieldCalculationType from API vs fieldCalculationType in frontend).
     */
    clonedItems: {
      handler(val) {
        if (!this.copy) {
          this.isChanged = false;
          this.isSaved = true;
          return;
        }
        if (val.dataTypeName === 'lookup') {
          if (!('RelatedEntity' in this.copy.dataTypeJSON) && 'RelatedEntity' in val.dataTypeJSON) {
            this.copy.dataTypeJSON.RelatedEntity = val.dataTypeJSON.RelatedEntity;
          }
        }
        this.isChanged = !isEqual(val, this.copy);
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...appMapState({
      isFourthVersion: state => state.isFourthVersion,
    }),
    ...dataManagementMapGetters(['fieldTypes']),
    ...dataManagementMapState({
      fields: state => state.fields,
      allFields: state => state.fields.data,
      fieldValues: state => state.fieldValues,
      fieldItemCalculationLoadingState: state => state.fieldItemCalculationLoadingState,
    }),
    fieldIsDisabled(){
      return this.isDisabled || this.isPosted;
    },
    clonedItems() {
      return JSON.parse(JSON.stringify(this.field));
    },
    fieldItemIndex() {
      return this.index + 1;
    },
    /** Does this field have any outstanding changes that require saving? */
    fieldRequiresSave() {
      /* NB Returning combined condition rather than discrete boolean values results in 'undefined'. */
      if ((!this.isPosted && this.isChanged) ? this.isChanged : (this.isChanged && !this.isSaved)) return true;
      if (this.field.name?.length === 0 && this.field.displayName !== 'Posted Status') return true;
      return false;
    },
    /** Does the Field have a Calculation defined. */
    fieldHasCalculation(){
      return entityUtils.fieldHasCalculation(this.field);
    },
    calculationDataValidationFailureMessage(){
      const calculationData = entityUtils.extractCalculationDataFromField(this.field);
      return entityUtils.calculationDataValidationFailureMessage(calculationData, this.field, this.allFields);
    },
    /** Is the Field allowed to have a Calculation defined. */
    fieldCalculationDeniedMessage(){
      return entityUtils.fieldCalculationDeniedMessage(this.field, this.$store);
    },
    /** Is the Field allowed to have a Calculation defined. */
    isFieldCalculationAllowed(){
      return entityUtils.isFieldCalculationAllowed(this.field, this.$store);
    },
    /**
     * Determine dynamic class names for Field Calculation Modal icon to use.
     * - Icon colour determines context:
     *   - Error (red)    - calculation has a validation error.
     *   - Exists (green) - calculation exists for field.
     *   - Allow (blue)   - calculation can be defined for Field.
     *   - Forbid (grey)  - calculation canot be defined for Field.
     */
    fieldCalculationModalIconClassNames(){
      const classNames = {};
      if (this.calculationDataValidationFailureMessage) classNames['text-danger'] = true;
      else if (this.fieldHasCalculation) classNames['text-success'] = true;
      else if (!this.isFieldCalculationAllowed) classNames['text-secondary'] = true;
      else classNames['text-primary'] = true;
      return classNames;
    },
  },
  methods: {
    ...dataManagementMapActions([
      'showFieldBuilderModal' ,
    ]),
    changePermission() {
      this.$emit('on-permission-click', this.field);
    },
    onSettingsClick() {
      /*
        Apr25 AH Previous version just assigned the field, but in the current version
        with the new Calculation Modal added, this caused the Field Modal to directly
        update the field which was immediately reflected in the Field Item.
        Hence clone the field so that state.builder.tableFieldModal.data is isolated.
        Previously: this.showFieldBuilderModal(this.field);
      */
      this.showFieldBuilderModal(JSON.parse(JSON.stringify(this.field)));
    },
    onDeleteClick() {
      this.$emit('field-delete', this.field);
    },
    getOptions(field) {
      let fields = [...this.fieldTypes];
      if (!(field.data && field.data.isPrimary)) {
        fields = fields.filter(elem => elem.value !== 'uuid');
      }
      if(!this.isFourthVersion){
        fields = fields.filter(el => el.value !== 'ai');
      }
      return fields;
    },
    handleFieldSave() {
      this.$emit('on-field-save');
      this.isSaved = true;

      // Update the local field copy with the newly saved values
      this.copy = cloneDeep(this.field);
    },
    handleFieldChange(fieldName, newValue) {
      this.isSaved = false;
      this.$emit('field-change', {fieldGuid: this.field.fieldGuid, fieldName, newValue});
    },
    /** Calculation icon click - show the Field Calculation modal. */
    onFieldCalculationModalClick() {
      /*
        Calculation cannot be created/updated while field has outstanding changes
        unless the Field's calculation has a validation error.
      */
      const validationFailureMessage = this.calculationDataValidationFailureMessage;
      if (this.fieldRequiresSave && !validationFailureMessage)
      {
        this.$bvModal.msgBoxOk('Calculation is not available until field changes are saved.', {variant:'primary'});
        return;
      }
      /*
        Check if field allows calculation.
        Skip if field already has a Calculation.
      */
      const deniedMessage = this.fieldCalculationDeniedMessage;
      if (deniedMessage && !entityUtils.fieldHasCalculation(this.field) && !validationFailureMessage)
      {
        /* Calculation not allowed on this field. */
        this.$bvModal.msgBoxOk(`Calculation is not available - ${deniedMessage}.`, {variant:'primary'});
        return;
      }
      this.showFieldCalculationModal(this.field);
    },
  },
  created(){
  },
  mounted() {
    /* Take a snapshot copy of the Field definition for later comparison. */
    this.copy = JSON.parse(JSON.stringify(this.field));
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../scss/variables";

.field-item {
  fieldset {
    width: 140px;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    margin-bottom: 0;
  }

  td {
    position: relative;
    padding-bottom: 0.5rem;
    padding-top: 0.5rem;
  }

  td:first-child {
    padding-left: 0.5rem;
  }

  td:last-child {
    padding-right: 0.5rem;
  }

  .invalid-feedback {
    position: absolute;
    margin-top: 0;
  }

  .btn-save-wrapper{
    padding: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .btn-save{
      margin: 0 12px;
    }

    .save-loading{
      margin: 0 10px;
    }
  }
}

.invalid-row {
  background-color: #F8D7DA;
}

.table-builder-field {
  flex-wrap: nowrap;

  label {
    white-space: nowrap;
  }

  .handle {
    text-align: center;
    cursor: move;
    width: $table-builder-field-handle-width;
    min-width: $table-builder-field-handle-width;
  }

  .btn-settings {
    padding-left: 0;
    padding-right: 0;
    width: $table-builder-field-settings-width;
    min-width: $table-builder-field-settings-width;
  }

  .btn-delete {
    padding-left: 0;
    padding-right: 0;
    width: $table-builder-field-delete-width;
    min-width: $table-builder-field-delete-width;
  }

  &.sortable-ghost {
    background-color: #F0F0F0;
    border-radius: 4px;
    /*border: 1px dotted #747474;*/
  }
}

.returned-invalid-feedback {
  display: block;
  margin-left: 31px;

  p {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.field-blocked-overlay {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.5);
}

.field-item {
  .icon-wrapper {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    bottom: -10px;
    left: 50%;
    cursor: pointer;
    transform: translate(calc(-50% + 3px));
    z-index: 111;


    .add-between-icon {
      position: absolute;
      width: 14px;
      height: 14px;
      cursor: pointer;
      padding: 2px;
      left: 50%;
      color: #7b8a8b;
      transform: translateX(-50%);
      transition: all .1s ease-in-out;
    }

    &:hover {
      .add-between-icon {
        width: 18px;
        height: 18px;
        color: #0a223d;
      }
    }
  }
}
</style>
